import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../_alert';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartmentService } from '../../services/jrni/department.service';
import { DatastoreService } from '../../services/datastore.service';
import { ServicesService } from '../../services/jrni/services.service';
import { TranslateService } from '@ngx-translate/core';
import { BasketService } from '../../services/jrni/basket.service';
import { ApiService } from '../../services/jrni/api.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { AppConfig } from "../../app.config";
import { HeyCentricService } from '../../services/hey-centric.service';
import { QuestionsService } from '../../services/questions.service';
import { interval, Subscription } from 'rxjs';

// HeyCentric return leg (book-after-payment). No booking exists when the
// customer lands here: bl-hey-centric creates it server-side once the gateway
// callback confirms, and this page only polls /pre-basket/find until the row
// reaches PAID. Return params:
//   online  — AuthCode / Rc / RecNo / Pmt   (Rc 'A' authorised, 'C' cancelled, 'U' unknown)
//   ARP     — return / payResult / authCode / refNo
//   no params, or Rc=C — the customer backed out of the gateway.
@Component({
  selector: 'app-verify-payment',
  templateUrl: './verify-payment.component.html',
  styleUrls: ['./verify-payment.component.scss']
})
export class VerifyPaymentComponent implements OnInit, OnDestroy {

  companyId: any = null;
  pageTitle: string;
  verifyPageText: string;
  selectedSlot: any;
  selectedService: any;
  selectedLocation: any;
  company: any;
  parent: any;
  config: any;
  basket: string;
  serviceItems: string[] = [];
  amount: any;
  receiptId: string;
  bookingMade = false;
  retries = 100;
  maxRetriesReached = false;
  paymentFailed = false;
  paymentAlreadyPaid = false;
  nr: any = window['newrelic'];
  webhookResponseStatus = '';
  chargeId = '';
  receipt = '';
  ref = '';
  failed = false;
  private pollSubs: Subscription[] = [];

  constructor(
    private router: Router,
    private departmentService: DepartmentService,
    private alertService: AlertService,
    private spinner: NgxSpinnerService,
    private datastoreService: DatastoreService,
    private heyCentricService: HeyCentricService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private servicesService: ServicesService,
    private basketService: BasketService,
    private apiService: ApiService,
    private availabilityService: AvailabilityService,
    private questionsService: QuestionsService
  ) { }

  async ngOnInit() {
    this.spinner.show();
    // Reaching the verify page means the HeyCentric round-trip concluded
    // (success or its own Back/Cancel button). Clear the payment-in-flight
    // marker so it can't later trigger a false journey reset. The browser Back
    // button never reaches this page, so its marker survives for app.component.
    sessionStorage.removeItem('heycentric_payment_pending');

    let configCompanyId = AppConfig.settings.core.company_id;
    if (this.datastoreService.isCitizenshipRoute) {
      configCompanyId = AppConfig.settings.core.citizenship_company_id;
    }

    const appConfig: any = await this.apiService.getConfig(configCompanyId);
    this.applyConfig(appConfig);

    this.route.params.subscribe(async (params) => {
      console.log('url params', params);
      if (params.basket) {
        this.basket = params.basket;
      }
      if (params.serviceItems) {
        // The return URL carries a leading-comma list (",<id>"); drop the empty head.
        this.serviceItems = params.serviceItems.split(',').filter((s) => s);
      }
      if (params.company) {
        this.companyId = params.company;
      }
      if (!(this.basket && this.serviceItems.length > 0)) {
        return;
      }
      console.log('begin verify');
      // The user abandoned the payment on the HeyCentric gateway. That happens
      // three ways: the browser Back button or HeyCentric's own "Back" button
      // (both return here with no query params), or HeyCentric's "Cancel" button
      // (returns with ?Rc=C plus Pmt/SID/hash). In all three cases the user would
      // otherwise get stuck on the "Verifying payment" spinner. Clear the current
      // basket and restart the journey on the landing page.
      const returnParams = this.route.snapshot.queryParams;
      if (Object.keys(returnParams).length < 1 || returnParams['Rc'] === 'C') {
        await this.restartJourneyAfterCancel();
        return;
      }
      this.route.queryParams.subscribe((queryParams) => {
        this.pageTitle = 'Verifying payment';

        this.departmentService.getParentDepartment().then(async (dept) => {
          await this.resolveLocation(dept);
          await this.handleReturn(queryParams);
        }).catch((err) => {
          console.log(err);
          this.spinner.hide();
          this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
        });
      });
    });
  }

  ngOnDestroy() {
    for (const sub of this.pollSubs) {
      sub.unsubscribe();
    }
    this.pollSubs = [];
  }

  private applyConfig(appConfig: any) {
    this.config = appConfig;
    this.datastoreService.config = appConfig;
    console.log('config', this.config);
    const configuredRetries = this.config?.journey_config?.appointments?.payment_verify_retries;
    if (configuredRetries !== undefined && configuredRetries !== null) {
      const parsed = parseInt(configuredRetries);
      if (!isNaN(parsed)) {
        this.retries = parsed;
        console.log(`Retries set to ${this.retries}`);
      }
    }
    // set page content to default
    this.datastoreService.pageContent = this.config?.journey_config?.appointments?.page_content?.["default"];
  }

  // Find the location (child company) the return URL names. The first pass uses
  // the current token's parent; if the location isn't one of its children this
  // was a citizenship booking — flip to the citizenship route, re-resolve the
  // parent under that token and re-read the config (the cache holds the wrong
  // parent's config at this point).
  private async resolveLocation(dept: any) {
    this.parent = dept;
    const originalChildren = await this.departmentService.getChildDepartments(dept);
    for (const location of originalChildren) {
      if (location.id == this.companyId) {
        this.company = location;
        await this.departmentService.setLocation(location);
        this.selectedLocation = location;
        break;
      }
    }
    console.log('location1', this.selectedLocation);
    if (!this.selectedLocation) {
      this.datastoreService.isCitizenshipRoute = true;
      this.parent = await this.departmentService.getParentDepartment();
      console.log('parent2', this.parent);
      const children = await this.departmentService.getChildDepartments(this.parent);
      for (const location of children) {
        if (location.id == this.companyId) {
          this.company = location;
          await this.departmentService.setLocation(location);
          this.selectedLocation = location;

          // redo the config
          this.apiService.configCache = {};
          const appConfig: any = await this.apiService.getConfig(this.parent.id);
          this.applyConfig(appConfig);
          break;
        }
      }
    }
    console.log('location2', this.selectedLocation);
  }

  private async handleReturn(params: any) {
    // Online (HeyCentric hosted page) return
    if (params['AuthCode'] && params['Rc'] && params['RecNo'] && params['Pmt']) {
      console.log(`AuthCode ${params['AuthCode']} Rc ${params['Rc']} RecNo ${params['RecNo']}`);
      if (this.nr) {
        this.nr.log(`PAYMENT_INFO`, { level: 'warn', customAttributes: { recNo: params['RecNo'], authCode: params['AuthCode'], Rc: params['Rc'], Pmt: params['Pmt'] } });
      }
      this.amount = parseFloat(params['Pmt']);
      if (params['Rc'] === 'A') {
        await this.verifyAuthorised(params['AuthCode'], params['RecNo'], false);
      } else {
        if (params['Rc'] === 'U' && this.nr) {
          // notify client of potential issue
          this.nr.log(`PAYMENT_UNKNOWN`, { level: 'error', customAttributes: { recNo: params['RecNo'], authCode: params['AuthCode'] } });
        }
        await this.markDeclined(params['AuthCode'], params['RecNo']);
      }
    } else if (params['Rc'] && params['Rc'] !== 'A') {
      // Declined/unknown without the full param set
      await this.markDeclined(params['AuthCode'], params['RecNo']);
    }

    // Telephony (Agent Referred Payment) return
    if (params['return']) {
      console.log('This is an ARP payment return');
      if (params['payResult']) {
        if (params['payResult'] === 'Authorised') {
          this.datastoreService.adminJourney = true;
          console.log(`payResult ${params['payResult']} authCode ${params['authCode']} refNo ${params['refNo']}`);
          await this.verifyAuthorised(params['authCode'], params['refNo'], true);
        } else {
          await this.markDeclined(params['authCode'] || params['AuthCode'], params['refNo'] || params['RecNo']);
        }
      }
    }
  }

  // Restore the journey state the summary page persisted on the purchase-log
  // row so the confirmation page can render without in-memory state.
  private async hydrateFromPurchase(purchase: any, withQuestions: boolean) {
    const extraItems = purchase.data?.extra_items;
    if (extraItems?.lookupResponses) {
      this.datastoreService.lookupResponses = extraItems.lookupResponses;
    }
    if (extraItems?.selectedAddress) {
      this.datastoreService.selectedAddress = extraItems.selectedAddress;
    }
    if (purchase.data?.auth_token) {
      localStorage.setItem('auth_token', purchase.data.auth_token);
    }
    if (purchase.data?.journey_route) {
      this.datastoreService.journeyRoute = purchase.data.journey_route;
      this.datastoreService.pageContent = this.config?.journey_config?.appointments?.page_content?.[purchase.data.journey_route];
    }
    this.selectedSlot = purchase.data?.slot;
    this.datastoreService.extraDuration = purchase.data?.extra_duration;
    if (withQuestions) {
      await this.getCompanyQuestions(purchase.data?.detail_group);
      await this.availabilityService.setSelectedSlot(this.selectedSlot);
    }
  }

  private showAlreadyPaid(purchase: any) {
    this.pageTitle = "We have already received your payment";
    this.ref = purchase.ref || this.basket;
    this.paymentAlreadyPaid = true;
    this.paymentFailed = true;
    this.spinner.hide();
    console.warn(`Purchase already paid`);
  }

  // Authorised return (online Rc=A, or ARP payResult=Authorised): hydrate from
  // the purchase-log row and poll it until the server-side worker reaches PAID.
  private async verifyAuthorised(authCode: string, receiptNo: string, arp: boolean) {
    for (const serviceItem of this.serviceItems) {
      try {
        console.log('serviceItem', serviceItem, 'cid', this.companyId);
        const purchase: any = await this.heyCentricService.preBasketFind(this.companyId, this.basket, serviceItem);
        if (!purchase) {
          console.error(`No purchase found for ${this.basket} and ${serviceItem}`);
          this.showVerifyUnavailable();
          continue;
        }
        // A reload of the online return URL after the booking was made: don't
        // re-run the flow. (ARP keeps polling — a fast PAID goes to confirmation.)
        if (!arp && purchase.status === 'PAID') {
          this.alertService.clear();
          if (await this.showConfirmationForPaidPurchase(purchase)) {
            return;
          }
          this.showAlreadyPaid(purchase);
          continue;
        }
        if (!(purchase.data && purchase.data.client_id)) {
          console.error(`No client found for ${this.basket} and ${serviceItem}`);
          continue;
        }
        await this.hydrateFromPurchase(purchase, true);
        purchase.data.auth_code = authCode;
        purchase.data.receipt = receiptNo;
        this.receiptId = receiptNo;
        // The online success path never writes RETURNED_FROM_GATEWAY from the
        // client: the callback owns the row and a client write can regress a
        // fast PAID. The ARP leg records the agent's gateway result on the row,
        // guarded to NEW for the same reason.
        if (arp) {
          purchase.data.agent = true;
          let fees = purchase.data.extra_items?.fees;
          if (purchase.status === 'NEW') {
            try {
              const update: any = await this.heyCentricService.preBasketUpdate(this.companyId, this.basket, serviceItem, 'RETURNED_FROM_GATEWAY', receiptNo, purchase.data);
              console.log('update', update);
              fees = update?.data?.extra_items?.fees || fees;
            } catch (err) {
              console.error('Could not stamp the ARP return on the purchase log', err);
            }
          }
          if (fees) {
            this.datastoreService.serviceFees = fees;
          }
        }
        this.startPolling(serviceItem, authCode, receiptNo);
      }
      catch (err) {
        console.error(err);
        this.showVerifyUnavailable();
      }
    }
  }

  // Declined / unknown / ARP-not-authorised return: stamp PAYMENT_FAILED on the
  // row (never over a PAID one) and show the cancelled panel.
  private async markDeclined(authCode: string, receiptNo: string) {
    this.pageTitle = "Your payment and booking has been cancelled";
    this.paymentFailed = true;

    for (const serviceItem of this.serviceItems) {
      try {
        console.log('serviceItem', serviceItem, 'cid', this.companyId);
        const purchase: any = await this.heyCentricService.preBasketFind(this.companyId, this.basket, serviceItem);
        if (!purchase) {
          console.error(`No purchase found for ${this.basket} and ${serviceItem}`);
          continue;
        }
        if (purchase.status === 'PAID') {
          this.showAlreadyPaid(purchase);
          continue;
        }
        if (!(purchase.data && purchase.data.client_id)) {
          console.error(`No client found for ${this.basket} and ${serviceItem}`);
          continue;
        }
        await this.hydrateFromPurchase(purchase, false);
        purchase.data.auth_code = authCode;
        purchase.data.receipt = receiptNo;
        this.receiptId = receiptNo;
        const update: any = await this.heyCentricService.preBasketUpdate(this.companyId, this.basket, serviceItem, 'PAYMENT_FAILED', receiptNo, purchase.data);
        console.log('updated', update);
      }
      catch (err) {
        console.error(err);
        this.spinner.hide();
        this.alertService.error(this.translateService.instant('PAYMENT.ERROR'));
      }
    }

    console.error(`Purchase was not successful for ${this.basket}.`);
    this.spinner.hide();
  }

  // Poll the purchase-log row every 2s: PAID → load the booking the worker made
  // and go to confirmation; *FAILED* → failed panel; retry budget exhausted →
  // "still confirming" banner with a reference to quote.
  private startPolling(serviceItem: string, authCode: string, receiptNo: string) {
    const sub = interval(2000).subscribe(async (val) => {
      console.log(`Run ${val}`);
      if (val >= this.retries) {
        console.error(`Retry limit of ${this.retries} reached`);
        sub.unsubscribe();
        this.pageTitle = 'We are still confirming your payment';
        this.webhookResponseStatus = '';
        this.alertService.clear();
        this.maxRetriesReached = true;
        this.spinner.hide();
        return;
      }
      if (this.bookingMade) {
        return;
      }
      try {
        const found: any = await this.heyCentricService.preBasketFind(this.companyId, this.basket, serviceItem);
        console.log('found in interval', found);
        this.webhookResponseStatus = `COMMON.${found.status}`;
        if (found.data) {
          if (found.data.auth_code) {
            this.chargeId = found.data.auth_code;
          }
          if (found.data.receipt) {
            this.receipt = found.data.receipt;
          }
        }
        if (found.ref) {
          this.ref = found.ref;
        }
        if (!this.chargeId && authCode) {
          this.chargeId = authCode;
        }
        if (!this.receipt && receiptNo) {
          this.receipt = receiptNo;
        }

        if (found.status === 'PAID') {
          sub.unsubscribe();
          this.bookingMade = true;
          await this.completeBooking(found);
        }
        else if (found.status && found.status.indexOf('FAILED') !== -1) {
          sub.unsubscribe();
          this.spinner.hide();
          this.webhookResponseStatus = '';
          this.failed = true;
        }
      }
      catch (err) {
        console.error(err);
        this.alertService.clear();
        this.webhookResponseStatus = 'COMMON.PAYMENT_VERIFY_RETRYING';
      }
    });
    this.pollSubs.push(sub);
  }

  private async completeBooking(found: any) {
    const purchase: any = await this.apiService.findPurchase(this.selectedLocation, found.data.purchase_id);
    const booking = purchase._embedded.bookings[0];
    this.datastoreService.booking = booking;
    const service = await this.apiService.getService(this.selectedLocation, booking.service_id);
    this.selectedService = service;
    this.datastoreService.selectedService = service;
    await this.servicesService.set(service);

    // set the date time, the location, the service and the booking on localstorage
    localStorage.setItem('booking', JSON.stringify(booking));
    localStorage.setItem('date', JSON.stringify(this.selectedSlot));
    localStorage.setItem('location', JSON.stringify(this.selectedLocation));
    localStorage.setItem('service', JSON.stringify(this.selectedService));

    this.router.navigate(['confirmation']);
  }

  // A record already PAID on arrival (a refresh after a slow verification):
  // send the customer to their confirmation instead of a dead end. Returns
  // false when the booking cannot be loaded, so the caller falls back to the
  // already-paid message.
  private async showConfirmationForPaidPurchase(purchase: any): Promise<boolean> {
    if (!purchase.data?.purchase_id) {
      return false;
    }
    try {
      await this.hydrateFromPurchase(purchase, true);
      await this.completeBooking(purchase);
      return true;
    }
    catch (err) {
      console.error(err);
      return false;
    }
  }

  private showVerifyUnavailable() {
    if (this.maxRetriesReached || this.paymentAlreadyPaid) {
      return;
    }
    this.pageTitle = 'We are still confirming your payment';
    this.webhookResponseStatus = '';
    this.alertService.clear();
    this.maxRetriesReached = true;
    this.spinner.hide();
  }

  // The user abandoned the payment on the gateway (Back/Cancel). Clear the
  // basket they abandoned and restart the journey on the correct landing page.
  async restartJourneyAfterCancel() {
    const authToken: string = localStorage.getItem('auth_token');
    try {
      await this.apiService.deleteBaskets({ id: this.companyId });
    }
    catch (err) {
      console.warn('Could not clear basket on payment cancel/back:', err);
    }
    this.basketService.currentBasket = null;
    if (localStorage.getItem('agent') && authToken) {
      // Agent/telephony journey: return to the admin services page.
      window.location.assign(`/services?admin-only=true&auth_token=${authToken}`);
    }
    else {
      // Public journey: return to the landing page for the journey they were on.
      const journey = sessionStorage.getItem('journey');
      window.location.assign(journey ? `/start/${journey}` : `/start`);
    }
  }

  async getCompanyQuestions(detailGroup) {
    this.questionsService.getCompanyQuestions(detailGroup).subscribe((res: any) => {
      // Questions = all questions on the company
      const questions = res.questions;
      this.datastoreService.companyQuestions = questions;
    });
  }

  restartJourney() {
    if (sessionStorage.getItem('journey')) {
      this.router.navigate(['start/' + sessionStorage.getItem('journey')])
    }
    else {
      this.router.navigate(['/departments']);
    }
  }

}
